import { CheckOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  CrashIcon,
  EmptyState,
  NotFoundIcon,
  PageHeader
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Radio } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { SecondSignaturePolicy } from './types'
import useEmarSecondSignatureSettings from './useEmarSecondSignatureSettings'
import useUpdateEmarSecondSignatureSettings from './useUpdateEmarSecondSignatureSettings'

const EMAR_SECOND_SIGNATURE_FLAG = 'emar_add_edit_medication_second_signature'

const OPTIONS: Array<{
  value: SecondSignaturePolicy
  labelKey: string
  descriptionKey: string
}> = [
  {
    value: SecondSignaturePolicy.Optional,
    labelKey: emarV2Constants.SECOND_SIGNATURE_OPTIONAL_LABEL,
    descriptionKey: emarV2Constants.SECOND_SIGNATURE_OPTIONAL_DESC
  },
  {
    value: SecondSignaturePolicy.RequiredForControlledDrugsOnly,
    labelKey: emarV2Constants.SECOND_SIGNATURE_CONTROLLED_LABEL,
    descriptionKey: emarV2Constants.SECOND_SIGNATURE_CONTROLLED_DESC
  },
  {
    value: SecondSignaturePolicy.RequiredForAllMedication,
    labelKey: emarV2Constants.SECOND_SIGNATURE_ALL_LABEL,
    descriptionKey: emarV2Constants.SECOND_SIGNATURE_ALL_DESC
  }
]

const SecondSignatureSettingsScreen = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isCareHomeAdmin = useAbilityAccess([
    { i: 'view', this: 'UserInvitation' }
  ])
  const { data: flags, isLoading: isFlagsLoading } = useFeatureFlags({
    flagNames: [EMAR_SECOND_SIGNATURE_FLAG]
  })
  const canAccess =
    isCareHomeAdmin && flags?.[EMAR_SECOND_SIGNATURE_FLAG]?.enabled

  const { requirement, isLoading, isError, refetch } =
    useEmarSecondSignatureSettings({ enabled: !isFlagsLoading && canAccess })

  if (isFlagsLoading || (canAccess && isLoading)) {
    return <Spinner />
  }

  if (!canAccess) {
    return (
      <EmptyState
        icon={<NotFoundIcon />}
        title={t(emarV2Constants.SECOND_SIGNATURE_NOT_FOUND)}
      />
    )
  }

  if (isError) {
    return (
      <EmptyState
        icon={<CrashIcon />}
        title={t(emarV2Constants.SECOND_SIGNATURE_LOAD_ERROR)}
      >
        <Button variant="default" onClick={() => refetch()}>
          {t(emarV2Constants.SECOND_SIGNATURE_RETRY)}
        </Button>
      </EmptyState>
    )
  }

  return <SecondSignatureSettingsForm initialRequirement={requirement} />
}

const SecondSignatureSettingsForm = ({
  initialRequirement
}: {
  initialRequirement: SecondSignaturePolicy
}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const [policy, setPolicy] =
    useState<SecondSignaturePolicy>(initialRequirement)

  const { updateSecondSignatureSettings, isLoading: isSaving } =
    useUpdateEmarSecondSignatureSettings()

  const isDirty = policy !== initialRequirement

  const handleSave = () => {
    if (!isDirty) {
      return
    }
    updateSecondSignatureSettings({ requirement: policy })
  }

  return (
    <StyledWrapper>
      <PageHeader title={t(emarV2Constants.SECOND_SIGNATURE_TITLE)} />
      <ContentCard>
        <IntroText>{t(emarV2Constants.SECOND_SIGNATURE_INTRO)}</IntroText>
        <Radio.Group
          value={policy}
          onChange={(e) => setPolicy(e.target.value)}
          style={{ display: 'contents' }}
        >
          <OptionsRow>
            {OPTIONS.map((option) => (
              <OptionCard
                key={option.value}
                $selected={policy === option.value}
              >
                <Radio value={option.value}>
                  <OptionLabel>{t(option.labelKey)}</OptionLabel>
                </Radio>
                <OptionDesc level={2}>{t(option.descriptionKey)}</OptionDesc>
              </OptionCard>
            ))}
          </OptionsRow>
        </Radio.Group>
        <OptionDesc level={2}>
          {t(emarV2Constants.SECOND_SIGNATURE_NOTE)}
        </OptionDesc>
      </ContentCard>
      <FixedFooter>
        <Button
          icon={<CheckOutlined />}
          variant="primary"
          disabled={!isDirty}
          isLoading={isSaving}
          onClick={handleSave}
        >
          {t(emarV2Constants.SECOND_SIGNATURE_SAVE_CHANGES)}
        </Button>
      </FixedFooter>
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 24px 30px 100px;
`

const ContentCard = styled(Card)`
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const IntroText = styled(Text)`
  color: ${({ theme }) => theme.color?.neutral?.color4};
`

const OptionsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`

const OptionCard = styled(Card)<{ $selected: boolean }>`
  display: flex;
  flex: 1;
  padding: 8px 12px 12px 12px;
  flex-direction: column;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.color?.status?.primary : theme.color?.neutral?.color7};

  .ant-radio-wrapper {
    margin: 0;
    padding: 0;
  }
`

const OptionLabel = styled(Text)``

const OptionDesc = styled(Text)`
  display: block;
  color: ${({ theme }) => theme.color?.neutral?.color4};
  margin-left: 24px;
`

const FixedFooter = styled.div.attrs({
  className: 'lmc-fixed-footer'
})`
  border: none;
  display: flex;
  justify-content: flex-end;
  width: calc(100vw - 290px);
  @media (max-width: 768px) {
    width: 100vw;
  }
`

export default SecondSignatureSettingsScreen
